import { IComboBoxOption } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPDefaultButton, IPLink, IPPrimaryButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ModifyTextDialog from 'components/Dialogs/ModifyTextDialog';
import SuccesMessage from 'components/Notification/SuccesMessage';
import RichTextReadOnly from 'components/Text/RichTextReadOnly';
import { CardToggle, ICardToggleOption } from 'components/Utils/CardToggle';
import { ComboWithFilter } from 'components/Utils/comboWithFilter';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapLargePlus5,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import { ISONorm } from 'models/norm';
import { TenantOnboarding } from 'models/tenant';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetDedupControlCount } from 'services/Api/controlService';
import { apiRequest } from 'services/Auth/authConfig';
import { sortOnString } from 'utils/sorting';
import { maxStandards } from './TrialWelcome';

interface ITrialWelcomeFrameworksProps {
  onboardingState: TenantOnboarding;
  standards: ISONorm[];
  onNext: (state: TenantOnboarding) => void;
  onPrev: (state: TenantOnboarding) => void;
}

const TrialWelcomeFrameworks = (props: ITrialWelcomeFrameworksProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'dashboard', 'adminCompany']);
  const [selectedKeys, setSelectedKeys] = useState<string[]>(
    props.onboardingState.onboardingSteps.certifiedStandards?.map((s) => String(s)) || [],
  );
  const [selectedOtherKeys, setSelectedOtherKeys] = useState<string[]>(
    props.onboardingState.onboardingSteps.newStandards?.map((s) => String(s)) || [],
  );
  const [showOtherFrameworksDialog, setShowOtherFrameworksDialog] = useState<boolean>(false);
  const [newFrameworksTemp, setNewFrameworksTemp] = useState<string | undefined>(undefined);
  const [newFrameworks, setNewFrameworks] = useState<string>(
    props.onboardingState.onboardingSteps.newOtherStandards || '',
  );
  const [dedupCount, setDedupCount] = useState<number>(0);

  const mostUsedFrameworksIds = [13, 1, 66, 17, 11, 36, 18, 31, 42];

  const hasReachedMaxStandards = selectedKeys.length + selectedOtherKeys.length >= maxStandards;

  useEffect(() => {
    const getDepupControls = async (ids: string[]) => {
      try {
        const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
        const dedupCount = await apiGetDedupControlCount(ids, accessToken);
        setDedupCount(dedupCount);
      } catch (err) {
        appContext.setError(err);
      }
    };

    getDepupControls([...selectedOtherKeys, ...selectedKeys]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys, selectedOtherKeys]);

  const applyNewState = (state: TenantOnboarding): TenantOnboarding => {
    const newOnboardingState = state.clone();
    const getUniqueStandardIds = (keys: string[]): number[] => [...new Set(keys.map((id) => parseInt(id)))];

    newOnboardingState.onboardingSteps.certifiedStandards = getUniqueStandardIds(selectedKeys);
    newOnboardingState.onboardingSteps.newStandards = getUniqueStandardIds(selectedOtherKeys);
    newOnboardingState.onboardingSteps.newOtherStandards = newFrameworks;
    newOnboardingState.onboardingSteps.wantsToCertify = true;

    return newOnboardingState;
  };

  const getCardOptions = (standardList: ISONorm[]): ICardToggleOption[] => {
    const options: ICardToggleOption[] = [];

    options.push(
      ...mostUsedFrameworksIds
        .filter((s) => standardList.some((s2) => s2.isoNormId === s))
        .map((m) => {
          const s = standardList.find((s) => s.isoNormId === m);

          return {
            key: `${s?.isoNormId}`,
            text: s?.name || '',
            subText: s?.shortDescription || '',
          };
        }),
    );

    //disable items for which an standard is selected that is an upgrade of that item
    options.forEach((option) => {
      const standard = standardList.find((s) => s.isoNormId.toString() === option.key);
      if (standard && standard.upgradeISONormId && !standard.allowDowngrade) {
        const upgrade = standardList.find((s) => s.isoNormId === standard.upgradeISONormId);
        if (upgrade && selectedKeys.includes(upgrade.isoNormId.toString())) {
          option.disabled = true;
        }
      }
    });

    //disable all options when max standards are reached
    if (hasReachedMaxStandards) {
      options.forEach((option) => {
        if (!selectedKeys.some((s) => s === option.key)) option.disabled = true;
      });
    }

    return options;
  };

  const getOtherOptions = (standardList: ISONorm[]): IComboBoxOption[] => {
    const options: IComboBoxOption[] = [];

    const availableOptions = standardList
      .filter((s) => !mostUsedFrameworksIds.some((id) => id === s.isoNormId))
      .filter((s) => !selectedKeys.some((id) => id === String(s.isoNormId)))
      .map((s) => {
        return {
          key: String(s.isoNormId),
          text: s?.name || '',
          title: s?.shortDescription || '',
        };
      });

    if (availableOptions.length > 0) {
      options.push(...availableOptions.sort((a, b) => sortOnString(a.text, b.text)));
    }

    return options;
  };

  const ansersAreNotValid = (): boolean => {
    if (newFrameworks.length > 0) return false;

    const totalSelected = selectedKeys.length + selectedOtherKeys.length;
    if (totalSelected === 0) return true;

    return false;
  };

  //
  // Main render
  //
  return (
    <IPStack styles={{ root: { width: 900, paddingRight: 20, paddingBottom: 20 } }}>
      <IPStack horizontalAlign="center" verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPStack verticalFill tokens={globalStackTokensGapLargePlus5}>
            <IPStack tokens={globalStackTokensGapExtraSmall}>
              <IPText block variant="large" styles={globalTextStylesBold}>
                {t('dashboard:TrialWelcome.Frameworks.Title')}
              </IPText>
              <IPText block>{t('dashboard:TrialWelcome.Frameworks.SubTitle')}</IPText>
            </IPStack>
            <IPStack.Item>
              <CardToggle
                options={getCardOptions(props.standards)}
                selectedKeys={selectedKeys}
                onSelect={(keys) => setSelectedKeys(keys as string[])}
                compact={true}
                multiSelect={true}
                allowEmptySelection={true}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPStack tokens={globalStackTokensGapSmall}>
                <ComboWithFilter
                  options={getOtherOptions(props.standards)}
                  noMatchText={t('dashboard:TrialWelcome.Frameworks.OtherStandardsNoMatch')}
                  selectedKeys={selectedOtherKeys}
                  onSelectedKeysChange={(keys) => setSelectedOtherKeys(keys)}
                  disabled={hasReachedMaxStandards}
                  placeholder={t('dashboard:TrialWelcome.Frameworks.OtherStandardsPlaceholder')}
                />
                {!newFrameworks && (
                  <IPStack.Item>
                    <IPLink onClick={() => setShowOtherFrameworksDialog(true)}>
                      {t('dashboard:TrialWelcome.Goals.AskNotInList')}
                    </IPLink>
                  </IPStack.Item>
                )}
                {newFrameworks && (
                  <IPStack.Item>
                    <RichTextReadOnly html={newFrameworks} onClick={() => setShowOtherFrameworksDialog(true)} />
                  </IPStack.Item>
                )}
                {dedupCount > 0 && (
                  <IPStack.Item>
                    <SuccesMessage
                      message={t('dashboard:TrialWelcome.Frameworks.DedupMessage', { count: dedupCount })}
                    />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
            <IPStack horizontal tokens={globalStackTokensGapMedium}>
              <IPStack.Item>
                <IPDefaultButton
                  text={t('dashboard:TrialWelcome.Goals.ButtonPrev')}
                  onClick={() => props.onPrev(applyNewState(props.onboardingState))}
                />
              </IPStack.Item>
              <IPStack.Item>
                <IPPrimaryButton
                  disabled={ansersAreNotValid()}
                  text={t('dashboard:TrialWelcome.Goals.ButtonNext')}
                  onClick={() => props.onNext(applyNewState(props.onboardingState))}
                />
              </IPStack.Item>
            </IPStack>
          </IPStack>
        </IPStack.Item>
      </IPStack>
      <ModifyTextDialog
        title={t('dashboard:TrialWelcome.Dialogs.AskNotInList.Title')}
        isOpen={showOtherFrameworksDialog}
        value={newFrameworksTemp ?? newFrameworks}
        placeholder={t('dashboard:TrialWelcome.Dialogs.AskNotInList.Placeholder')}
        onUpdate={setNewFrameworksTemp}
        onClose={() => {
          setShowOtherFrameworksDialog(false);
          setNewFrameworksTemp(undefined);
        }}
        onSave={(value) => {
          setNewFrameworks(value || '');
          setNewFrameworksTemp(undefined);
          setShowOtherFrameworksDialog(false);
        }}
        multiline
        maxLength={256}
        width={appContext.isMobileView ? 300 : 600}
      />
    </IPStack>
  );
};

export default TrialWelcomeFrameworks;
